import Decimal from 'decimal.js-light';
import { useRouter } from 'next/router';
import type { Query } from 'nextjs-routes';
import { useEffect, useRef, useState } from 'react';
import { useDebounce, useIsomorphicLayoutEffect, usePrevious } from 'react-use';

import { tokenFormatter } from '@/lib/formatting';

import type { TokenInputValues } from '@/components/TransactionModule/store';

import { routes } from '@/utils/routes';
import { tradeTypeFromPathname } from '@/utils/url';

const amountsEqual = (a: string, b: string) => {
  // new Decimal() throws on non-numerical strings; unparsable = not equal.
  try {
    return new Decimal(a).eq(new Decimal(b));
  } catch {
    return false;
  }
};

type InputAmountQueryState = {
  sellInput: TokenInputValues;
  sellTokenUsd: number;
  updateSellInput: (value: TokenInputValues) => void;
};

/** Initializes sell input from `sellAmount` once and passively mirrors later edits to the URL. */
export function useInputAmountQuerySync({
  sellInput,
  sellTokenUsd,
  updateSellInput,
}: InputAmountQueryState): void {
  const router = useRouter();
  const latestRouter = useRef(router);
  useIsomorphicLayoutEffect(() => {
    latestRouter.current = router;
  }, [router]);
  const [debouncedInputDecimal, setDebouncedInputDecimal] = useState('');
  useDebounce(() => setDebouncedInputDecimal(sellInput.decimal), 500, [sellInput.decimal]);
  const previousDebouncedInputDecimal = usePrevious(debouncedInputDecimal);
  const previousSellDecimal = usePrevious(sellInput.decimal);
  const seededAmountRef = useRef<string | null>(null);
  const pendingWritesRef = useRef(new Set<{ amount: string }>());

  // Default sellAmount to empty string to avoid calling the `replace` function on mount
  const {
    query: { sellAmount = '' },
  } = router;

  const ignoreUpdate =
    previousDebouncedInputDecimal === undefined ||
    previousDebouncedInputDecimal === debouncedInputDecimal ||
    sellAmount === debouncedInputDecimal;

  useEffect(() => {
    if (!router.isReady || Array.isArray(sellAmount)) return;

    // A delayed shallow replace mirrors an earlier edit; it must not seed over
    // newer input, even when that input has already passed the debounce.
    const ownWrites = [...pendingWritesRef.current].filter((write) => write.amount === sellAmount);
    if (ownWrites.length > 0) {
      ownWrites.forEach((write) => pendingWritesRef.current.delete(write));
      seededAmountRef.current = sellAmount;
      return;
    }

    const isInitialSeed = seededAmountRef.current === null;
    if (!isInitialSeed && seededAmountRef.current === sellAmount) return;

    if (!sellAmount) {
      seededAmountRef.current = '';
      return;
    }

    if (sellInput.decimal !== '') {
      if (amountsEqual(sellInput.decimal, sellAmount)) {
        seededAmountRef.current = sellAmount;
        return;
      }
      const userTypedBeforeInitialSeed =
        isInitialSeed &&
        previousSellDecimal !== undefined &&
        previousSellDecimal !== sellInput.decimal;
      if (userTypedBeforeInitialSeed) {
        seededAmountRef.current = sellAmount;
        return;
      }
    }

    if (sellTokenUsd === 0) return;

    seededAmountRef.current = sellAmount;

    try {
      const decimal = new Decimal(sellAmount).toString();
      updateSellInput({
        decimal,
        formatted: tokenFormatter.format(decimal, { mode: 'write' }),
        usd: new Decimal(sellAmount).mul(sellTokenUsd).toNumber(),
      });
    } catch {}
  }, [
    router.isReady,
    sellAmount,
    sellInput.decimal,
    previousSellDecimal,
    updateSellInput,
    // The seed only reads the sell price; depending on the whole object would
    // re-run the effect on every price refresh for the page's lifetime.
    sellTokenUsd,
  ]);

  useEffect(() => {
    if (!router.isReady || ignoreUpdate) return;

    const { sellAmount: _sellAmount, ...currentQuery } = router.query;
    const hasPair = !!currentQuery.sellAddress || !!currentQuery.buyAddress;
    // Homepage and trade routes keep short paths; only the amount is written.
    const isTradeRoute =
      router.pathname === routes.INDEX || !!tradeTypeFromPathname(router.pathname);
    if (!hasPair && !isTradeRoute) return;

    const newQuery: Query = { sellAmount: debouncedInputDecimal };
    if (!newQuery.sellAmount) delete newQuery.sellAmount;

    const write = { amount: debouncedInputDecimal };
    pendingWritesRef.current.add(write);

    router
      .replace(
        {
          pathname: router.pathname,
          query: {
            ...currentQuery,
            ...newQuery,
          },
        },
        undefined,
        { shallow: true },
      )
      .then(
        (completed) => {
          // Next can finish navigation before the seeding effect observes it.
          if (completed && (latestRouter.current.query.sellAmount ?? '') === write.amount) {
            seededAmountRef.current = write.amount;
          }
          pendingWritesRef.current.delete(write);
        },
        () => pendingWritesRef.current.delete(write),
      );
  }, [debouncedInputDecimal, ignoreUpdate, latestRouter, router]);
}
